/**
 * Orchestrator.js
 * The "Conductor" of the system.
 * Determines which instruments are active based on Narrative Curves (Tension/Intensity).
 * Replaces deterministic matrices with probabilistic activation thresholds.
 */

import { Random } from '../Random.js';
import { GENRE_ENSEMBLES, DEFAULT_ENSEMBLE, MINIMAL_FALLBACK } from '../../data/EnsembleRegistry.js';
import { getInstrument, getAllInstruments } from '../../data/InstrumentRegistry.js';
import { MINIMUM_VIABLE_TOPOLOGY, ROLE_KEYWORDS } from './TopologyDefinitions.js';

// Helper for fuzzing values
const fuzz = (val, variance, random) => {
    return val + ((random.next() * variance * 2) - variance);
};

/**
 * Assigns a unique "Personality" to each instrument instance.
 * Defines how they react to global tension.
 * @param {Array} instruments - List of instrument objects.
 * @param {number} seed - Random seed.
 * @returns {Map} Map<InstanceId, PersonalityObject>
 */
export const generateInstrumentPersonalities = (instruments, seed, config) => {
    const random = new Random(seed);
    const personalities = new Map();
    const curves = config?.orchestration?.activationCurves || {};

    instruments.forEach(inst => {
        // Default Sensitivity
        let sensitivity = 1.0;
        // Default Threshold (0.0 = Always On, 1.0 = Only Climax)
        let activationThreshold = 0.5;

        // Correctly access traits (merged from capabilities and base traits)
        const capTraits = inst.capabilities?.traits || {};
        const baseTraits = inst.traits || {};
        const traits = { ...baseTraits, ...capTraits };

        const role = inst.roles ? inst.roles[0] : 'harmony';

        // Infer family if missing
        let family = traits.family;
        if (!family) {
            if (inst.category === 'Orchestral') {
                if (inst.id.includes('violin') || inst.id.includes('cello') || inst.id.includes('viola') || inst.id.includes('bass')) family = 'strings';
                else if (inst.id.includes('flute') || inst.id.includes('clarinet') || inst.id.includes('oboe') || inst.id.includes('bassoon')) family = 'woodwinds';
                else if (inst.id.includes('trumpet') || inst.id.includes('horn') || inst.id.includes('tuba') || inst.id.includes('trombone')) family = 'brass';
                else if (inst.id.includes('timpani') || inst.id.includes('perc')) family = 'percussion';
            } else {
                if (inst.id.includes('guitar')) family = 'plucked';
                else if (inst.id.includes('piano') || inst.id.includes('keys')) family = 'keys';
                else if (inst.id.includes('synth') || inst.id.includes('pad')) family = 'synth';
            }
        }
        if (!family) family = 'other';

        // 1. Config-Driven Activation Curves
        // Check family first (strings, brass, etc)
        if (curves[family]) {
            activationThreshold = curves[family].threshold;
            sensitivity = curves[family].slope || 1.0;
        } else if (family === 'other') {
            // Fix: "Sporadic Intro" - Lower default threshold for unknown instruments so they aren't silenced in Intro
            activationThreshold = 0.2;
        }
        // Fallback or overrides based on specific traits
        else if (inst.category === 'Orchestral') {
            const isBass = traits.spectral_role === 'low' || role.includes('bass') || inst.id.includes('bass') || inst.id.includes('cello');
            if (isBass) {
                // Bass/Cellos: Foundation, always present early
                activationThreshold = 0.1;
                sensitivity = 0.5; // Steady
            } else if (family === 'percussion') {
                 activationThreshold = 0.0;
                 sensitivity = 1.0;
            } else if (family === 'woodwinds' || inst.id.includes('flute') || inst.id.includes('clarinet')) {
                 // Fix: "Role-Based Thresholds" - Lowered from 0.2 to 0.15 to allow easier entry
                 activationThreshold = 0.15;
            } else if (family === 'strings' || inst.id.includes('violin') || inst.id.includes('viola')) {
                 // Fix: "Role-Based Thresholds" - Strings are Core (0.1)
                 activationThreshold = 0.1;
            } else if (family === 'brass') {
                 // Fix: "Role-Based Thresholds" - Brass is Color (0.3)
                 activationThreshold = 0.3;
            }
        } else {
            // Band / Electronic Overrides
            // Map types to curves if possible
            if (role.includes('bass')) {
                activationThreshold = 0.0;
            } else if (role.includes('lead') || role.includes('solo')) {
                activationThreshold = 0.7; // Solos only at high energy
            } else if (role.includes('pad') || role.includes('harmony')) {
                if (curves.synth) {
                    activationThreshold = curves.synth.threshold;
                    sensitivity = curves.synth.slope;
                } else {
                    activationThreshold = 0.1; // Core role
                }
            } else if (inst.id.includes('distortion') || inst.id.includes('drive')) {
                activationThreshold = 0.6;
            } else if (curves.plucked && (family === 'plucked' || family === 'keys')) {
                activationThreshold = curves.plucked.threshold;
                sensitivity = curves.plucked.slope;
            }
        }

        // 2. Random Variance (The "Personality")
        // Some players are shy (higher threshold), some eager (lower)
        // Use Gaussian distribution for more natural clustering
        // Assuming Random class has Gaussian from previous batch
        const gRandom = (val, variance) => {
            if (random.gaussian) return random.gaussian(val, variance);
            return fuzz(val, variance, random);
        };

        activationThreshold = Math.max(0.0, Math.min(0.95, gRandom(activationThreshold, 0.05)));
        sensitivity = Math.max(0.5, Math.min(2.0, gRandom(sensitivity, 0.1)));

        personalities.set(inst.instanceId || inst.id, {
            activationThreshold,
            sensitivity,
            bias: random.range(-0.1, 0.1) // Random bias
        });
    });

    return personalities;
};

/**
 * Determines if an instrument should be active for a given context.
 * @param {Object} instrument - The instrument object.
 * @param {Object} personality - The personality object.
 * @param {number} tension - Global tension (0.0 - 1.0).
 * @param {Object} sectionAffinities - Map of section affinities for this genre.
 * @param {string} sectionType - Current section type (INTRO, VERSE, etc).
 * @param {Object} random - Random instance.
 * @returns {boolean} True if active.
 */
export const evaluateInstrumentActivation = (instrument, personality, tension, sectionAffinities, sectionType, random) => {
    if (!personality) return true; // Fallback

    // 1. Calculate Score
    // Base Score: (Tension * Sensitivity) -> Non-Linear
    // Fix: "Ghost Town" & "Floor Lift" - Further reduced exponent to 0.5 (Square Root) to ensure score > 0.3 at T=0.1.
    // score = tension^0.5 * sensitivity
    let score = Math.pow(tension, 0.5) * personality.sensitivity;

    // 2. Apply Section Affinity (Context is King)
    // sectionAffinities: { INTRO: { strings: 0.8, brass: 0.1 } }
    if (sectionAffinities && sectionType) {
        const affinities = sectionAffinities[sectionType];
        if (affinities) {
            // Check by Family
            const family = instrument.traits?.family; // 'strings', 'brass'
            if (family && affinities[family] !== undefined) {
                // Affinity acts as a booster or suppressor
                // 0.5 is neutral. >0.5 boosts, <0.5 suppresses.
                const affinity = affinities[family];
                score += (affinity - 0.5);
            }

            // Check by Trait (e.g. 'plucked')
            // We iterate keys of affinities and check if instrument has that trait or id match
            Object.keys(affinities).forEach(key => {
                if (key === family) return; // Handled
                if (instrument.id.includes(key) || (instrument.category && instrument.category.toLowerCase().includes(key))) {
                     score += (affinities[key] - 0.5);
                }
            });
        }
    }

    // 3. Random Flux (Breathing)
    score += personality.bias;
    score += random.range(-0.05, 0.05);

    // 4. Threshold Check
    // Dynamic Threshold Scaling:
    // If tension is extremely low (Intro), we lower the effective threshold for 'Lead' or 'Pad'
    // so *someone* plays.
    // But calculateScore already factors tension * sensitivity.
    // Issue: If tension is 0.2 and sensitivity 1.0, score is 0.2.
    // If threshold is 0.5, it fails.
    // We need to either boost score at low tension for certain roles, or rely on Minimum Viable Presence.
    // The scoring model assumes Tension is the driver.

    return score >= personality.activationThreshold;
};

/**
 * Generates a map of active instruments per section for the entire structure.
 * @param {Array} structure - Song structure.
 * @param {Array} instruments - Active instruments.
 * @param {Object} config - Seeded config (contains affinities).
 * @param {number} seed - Seed.
 * @returns {Map} Map<SectionType, Set<InstanceId>>
 */
const calculateInstrumentPriority = (instrument, sectionType, config, intensity = 0.5) => {
    let priority = 0;
    const roles = instrument.roles || [];
    const traits = instrument.traits || {};

    // Directive 2: Priority Hierarchy
    // 1. Foundational (High Priority)
    if (roles.includes('bass') || roles.includes('drums') || roles.includes('pad') || roles.includes('foundation')) {
        priority += 30;
        // Fix: Smooth Intensity Boost (No Hard Snap at 0.7)
        priority += intensity * 15;
    }
    // 2. Texture (Medium Priority)
    else if (roles.includes('harmony') || roles.includes('rhythm_guitar') || traits.family === 'keys' || traits.family === 'plucked') {
        // Baroque Harpsichord is foundational
        if (config && config.harmonySource === 'baroque' && traits.family === 'keys') {
            priority += 40; // Higher than bass to ensure presence
        } else {
            priority += 20;
        }
    }
    // 3. Color (Low Priority)
    else {
        priority += 10;
    }

    // 4. Melody (Contextual Priority)
    // Fix: "Melody Mute" - Boost melody instruments in Intro/Verse to ensure they survive the cut.
    if (roles.includes('melody') || roles.includes('lead')) {
        // Constraint Fix: Dynamic Boost
        // Base boost of 40 (was 15) to ensure Melody outranks Bass (30) and Harmony (20)
        // Scalable by config if provided
        const baseBoost = (config && config.orchestration && config.orchestration.melodyPriority) || 40;

        if (sectionType.includes('INTRO') || sectionType.includes('VERSE') || sectionType.includes('SECTION') || sectionType.includes('THEME')) {
            // Baroque Melody Force
            if (config && config.harmonySource === 'baroque') priority += 60; // Total 100+
            else priority += baseBoost;
        } else {
            // Even in non-Melody focus sections, keep priority reasonable so it doesn't vanish
            priority += 20;
        }
    }

    // Impressionist Bass Force (Ensure Contrabass/Cello anchors the texture even in soft sections)
    if (config && config.harmonySource === 'impressionist' && (roles.includes('bass') || roles.includes('foundation'))) {
        priority += 50;
    }

    // Section Affinity Bonus
    const affinities = config?.orchestration?.sectionAffinities?.[sectionType];
    if (affinities && traits.family && affinities[traits.family] !== undefined) {
        priority += (affinities[traits.family] - 0.5) * 10;
    }

    // Role Rotation (Texture Variation)
    // For "Color" or "Texture" roles, apply a random rotation bias based on ID hash and section intensity
    // This ensures that in lower intensity sections, different instruments take turns (e.g. Strings vs Winds).
    if (!roles.includes('bass') && !roles.includes('drums') && !roles.includes('lead') && !roles.includes('melody')) {
        // Only rotate if intensity is moderate/low (allow saturation at high intensity)
        if (intensity < 0.8) {
            // Hash instrument ID + Section Type
            let hash = 0;
            const str = instrument.id + sectionType;
            for (let i = 0; i < str.length; i++) hash = (hash << 5) - hash + str.charCodeAt(i);
            hash = Math.abs(hash);

            // 50% chance to get a small boost or penalty
            const rotation = (hash % 100) / 100; // 0.0 - 1.0
            priority += (rotation - 0.5) * 20; // +/- 10
        }
    }

    return priority;
};

/**
 * Generates a map of active instruments per BAR for the entire structure.
 * REPLACES determineSectionInstrumentation with finer granularity (Phase 5).
 * @param {Array} structure - Song structure.
 * @param {Array} instruments - Active instruments.
 * @param {Object} config - Seeded config.
 * @param {number} seed - Random seed.
 * @param {Array} intensityCurve - Global intensity curve (one float per bar).
 * @returns {Map} Map<BarIndex, Set<InstanceId>>
 */
export const determineDynamicInstrumentation = (structure, instruments, config, seed, intensityCurve) => {
    const random = new Random(seed);
    const resultMap = new Map();

    let previousActiveSet = new Set();
    let currentBarIndex = 0;

    structure.forEach((section, sectionIndex) => {
        const type = section.type.toUpperCase();
        const bars = section.bars || 4;

        for (let b = 0; b < bars; b++) {
            const absoluteBarIndex = currentBarIndex + b;

            // Get Intensity for this bar from curve
            // Fix: Robust check for intensityCurve existence to prevent null property access
            const intensity = (intensityCurve && intensityCurve[absoluteBarIndex] !== undefined)
                              ? intensityCurve[absoluteBarIndex]
                              : 0.5;

            // Directive 1: Target Count Strategy (Linear Ramp based on Intensity)
            const totalInstruments = instruments.length;
            // Skeleton Crew: Bass + Melody + 1 (Ensure trio at minimum)
            const minCount = Math.min(totalInstruments, 3);

            // Directive 1: Target Count Strategy
            // Use a Power Curve for Saturation:
            // 0.0 -> minCount
            // 0.8 -> 90%
            // 0.9 -> 100%
            // This removes the "Hard Snap" at 0.85 which caused the stripey artifact when intensity dithered.

            // Intensity ^ 0.5 (Sqrt) pushes the curve up faster, ensuring fullness earlier.
            // But we want saturation at the top.
            // Let's use a modified linear map that hits max at 0.9.
            let effectiveIntensity = Math.min(1.0, intensity / 0.9); // 0.9 becomes 1.0

            // Phase 2: Temporal Scaling (Short Section Boost)
            // If section is short (< 8 bars), accelerate the curve to ensure it resolves.
            if (bars < 8) {
                // Power curve (0.7) lifts the mid-range values
                effectiveIntensity = Math.pow(effectiveIntensity, 0.7);
            }

            // Phase 3: Genre Floor (Density Override)
            // For High Energy sections, raise the floor (minCount)
            let dynamicMinCount = minCount;
            if (type.includes('BUILD') || type.includes('CLIMAX') || type.includes('DROP')) {
                // Start with at least 50% of the ensemble
                dynamicMinCount = Math.max(minCount, Math.floor(totalInstruments * 0.5));
            }

            let targetCount = Math.floor(dynamicMinCount + (totalInstruments - dynamicMinCount) * effectiveIntensity);

            // Explicit Climax Lock
            // If section is Climax/Drop, force max.
            if (type.includes('CLIMAX') || type.includes('DROP')) {
                targetCount = totalInstruments;
            }

            const finalTarget = Math.max(dynamicMinCount, Math.min(totalInstruments, targetCount));

            // Score instruments
            const scoredCandidates = instruments.map(inst => {
                const id = inst.instanceId || inst.id;
                // Use section type for affinity scoring still (as genres are section-based)
                let score = calculateInstrumentPriority(inst, type, config, intensity);

                // Directive 3: Stability Check (Hysteresis)
                if (previousActiveSet.has(id)) {
                    // Huge bonus to prevent flickering (Stripey behavior)
                    // Unless intensity dropped significantly, we want to keep them.
                    score += 40;
                }

                // Random Flux (Reduced to stabilize active set)
                // Was: +/- 2. Now: +/- 0.5 (5% of a priority step)
                score += random.range(-0.5, 0.5);

                return { id, score };
            });

            // Sort descending
            scoredCandidates.sort((a, b) => b.score - a.score);

            // Select Top N
            const activeSet = new Set();
            for (let i = 0; i < finalTarget; i++) {
                if (scoredCandidates[i]) {
                    activeSet.add(scoredCandidates[i].id);
                }
            }

            // Phase 1: Minimum Viable Topology (MVT) Enforcement
            // Ensure critical roles for this section type are present
            const mvtDef = MINIMUM_VIABLE_TOPOLOGY[type] || MINIMUM_VIABLE_TOPOLOGY['VERSE']; // Fallback

            if (mvtDef && mvtDef.requiredRoles) {
                mvtDef.requiredRoles.forEach(reqRoleKey => {
                    // Check if role is present in activeSet
                    // reqRoleKey maps to a list of keywords (e.g., 'riser' -> ['fx', 'riser'])
                    const keywords = ROLE_KEYWORDS[reqRoleKey] || [reqRoleKey];

                    const isSatisfied = [...activeSet].some(id => {
                        const inst = instruments.find(i => (i && (i.instanceId || i.id) === id));
                        if (!inst) return false;

                        // Check roles and ID
                        const roles = inst.roles || [];
                        const matchesKeyword = keywords.some(k =>
                            inst.id.toLowerCase().includes(k) ||
                            (inst.category && inst.category.toLowerCase().includes(k)) ||
                            roles.some(r => r.includes(k))
                        );
                        return matchesKeyword;
                    });

                    if (!isSatisfied) {
                        // Force Recruitment
                        // Search candidates (even those not selected)
                        const candidate = scoredCandidates.find(c => {
                            if (activeSet.has(c.id)) return false; // Already active (should have been caught above, but safety)
                            const inst = instruments.find(i => (i && (i.instanceId || i.id) === c.id));
                            if (!inst) return false;

                            const roles = inst.roles || [];
                            return keywords.some(k =>
                                inst.id.toLowerCase().includes(k) ||
                                (inst.category && inst.category.toLowerCase().includes(k)) ||
                                roles.some(r => r.includes(k))
                            );
                        });

                        if (candidate) {
                            activeSet.add(candidate.id);
                            // console.log(`[Orchestrator] Force-Recruited ${candidate.id} for role ${reqRoleKey} in ${type}`);
                        }
                    }
                });
            }

            // Phase 2B: Role Call Safety Net (Spectrum Balancing)
            // If Tension is high (> 0.5) and Low Spectrum is missing, force Bass.
            if (intensity > 0.5) {
                const hasLow = [...activeSet].some(id => {
                    const inst = instruments.find(i => (i && (i.instanceId || i.id) === id));
                    return inst && (inst.traits?.spectral_role === 'low' || (inst.roles && inst.roles.includes('bass')));
                });

                if (!hasLow) {
                    const bestBass = scoredCandidates.find(c => {
                        if (activeSet.has(c.id)) return false;
                        const inst = instruments.find(i => (i && (i.instanceId || i.id) === c.id));
                        return inst && (inst.traits?.spectral_role === 'low' || (inst.roles && inst.roles.includes('bass')));
                    });
                    if (bestBass) activeSet.add(bestBass.id);
                }
            }

            // Fix: Minimum Viable Presence (Melody Protection)
            // Kept as final fallback
            const isDrumBreak = type.includes('BREAK') || type.includes('PERC');
            if (!isDrumBreak) {
                const hasMelody = [...activeSet].some(id => {
                    const inst = instruments.find(i => (i && (i.instanceId || i.id) === id));
                    return inst && inst.roles && (inst.roles.includes('melody') || inst.roles.includes('lead'));
                });

                if (!hasMelody) {
                    const bestMelody = scoredCandidates.find(c => {
                        const inst = instruments.find(i => (i && (i.instanceId || i.id) === c.id));
                        return inst && inst.roles && (inst.roles.includes('melody') || inst.roles.includes('lead')) && !activeSet.has(c.id);
                    });
                    if (bestMelody) activeSet.add(bestMelody.id);
                }
            }

            // Save Result (Unique Key: absoluteBarIndex)
            resultMap.set(absoluteBarIndex, activeSet);

            // Update History
            previousActiveSet = activeSet;
        }
        currentBarIndex += bars;
    });

    return resultMap;
};

// Deprecated: Legacy Function (Maintained for backward compatibility or direct calls)
export const determineSectionInstrumentation = (structure, instruments, config, seed) => {
    // Legacy behavior: calculates mostly static sets per section type
    // We can just call dynamic with a flat curve if we wanted, but let's keep original logic for safety.
    return determineDynamicInstrumentation(structure, instruments, config, seed, null);
};

/**
 * Generates a Global Intensity Curve for the entire structure.
 * Task 1: "Curve Audit" - Implements Compound Intensity Formula.
 * @param {Array} structure - The song structure array.
 * @param {number} globalDensity - User density setting (0-1). Default 0.5.
 * @param {Object} random - Optional random instance for fuzziness.
 * @returns {Array} - Array of floats (one per bar) representing target intensity (0.0 - 1.0).
 */
export const getSectionIntensityCurve = (structure, globalDensity = 0.5, random = null) => {
    if (!structure || structure.length === 0) return [0.5];

    const curve = [];

    // Base Narrative Tension Map
    const tensionMap = {
        INTRO: 0.2,
        VERSE: 0.4,
        PRE_CHORUS: 0.6,
        CHORUS: 0.8,
        BRIDGE: 0.5,
        SOLO: 0.85,
        CLIMAX: 0.95,
        OUTRO: 0.3,
        DROP: 0.9,
        BUILD: 0.7
    };

    structure.forEach((section, index) => {
        const bars = section.bars || 4;
        const type = section.type.toUpperCase();
        // Fallback for custom sections
        const baseTension = tensionMap[type] || (type.includes('VERSE') ? 0.4 : 0.6);

        for (let b = 0; b < bars; b++) {
            const progress = b / bars; // 0.0 to 1.0

            // Task 1: Compound Intensity Formula
            // intensity = (tension * 0.6) + (progress * 0.2) + (density * 0.2)
            // Note: We use 'baseTension' as the narrative tension anchor.
            // We adjust 'baseTension' slightly based on progress for internal dynamics (e.g. Build rises).

            let currentTension = baseTension;

            // Apply Micro-Dynamics to Tension before formula
            if (type.includes('BUILD') || type.includes('RISE')) {
                currentTension = 0.4 + (Math.pow(progress, 2) * 0.6);
            } else if (type.includes('OUTRO')) {
                currentTension = baseTension * (1 - progress);
            } else if (type.includes('INTRO')) {
                currentTension = 0.2 + (progress * 0.3);
            }

            // Apply The Formula
            // We normalize progress to be meaningful (it's 0-1 per section).
            // We use globalDensity to lift the floor or ceiling.
            // Increased globalDensity influence to 0.4 to satisfy sensitivity tests (Energy Slider)
            let intensity = (currentTension * 0.5) + (progress * 0.1) + (globalDensity * 0.4);

            // Fuzziness (Contextual Awareness)
            // FIX: Removed Intensity Jitter to prevent "Stripey" flickering artifacts.
            // The curve logic above provides enough variance.

            // Restrictive Boundaries: Raise Floor, Lower Ceiling
            intensity = Math.max(0.2, Math.min(0.95, intensity));

            curve.push(intensity);
        }
    });

    // Smooth
    for (let i = 1; i < curve.length - 1; i++) {
        curve[i] = (curve[i-1] + curve[i] + curve[i+1]) / 3;
    }

    // Task 4: The "Smooth Handoff" (Transition Ramps)
    calculateTransitionCurve(curve, structure);

    return curve;
};

// Task 4 Helper: Calculate Transition Curve
const calculateTransitionCurve = (curve, structure) => {
    let barOffset = 0;
    for (let i = 0; i < structure.length - 1; i++) {
        const section = structure[i];
        barOffset += (section.bars || 4);

        // Boundary indices
        // We want to ramp during the LAST bar of the current section (A)
        // so that it meets the FIRST bar of the next section (B).
        const lastBarIdx = barOffset - 1;
        const nextBarIdx = barOffset;

        if (nextBarIdx < curve.length) {
            // Generate 1-Bar Linear Ramp
            // Interpolate between the previous stable value (bar before last) and the next target
            const prevValue = (lastBarIdx > 0) ? curve[lastBarIdx - 1] : curve[lastBarIdx];
            const targetValue = curve[nextBarIdx];

            // Set the transition bar to the midpoint
            curve[lastBarIdx] = (prevValue + targetValue) / 2;
        }
    }
};

/**
 * Selects a curated ensemble for a specific genre.
 * Replaces random instrument selection with cohesive, pre-defined bands.
 * @param {string} genre - Target genre (e.g. 'Cinematic').
 * @param {number} seed - Random seed.
 * @returns {Array} List of Instrument Objects.
 */
export const selectEnsembleForGenre = (genre, seed) => {
    const random = new Random(seed);
    let genreKey = Object.keys(GENRE_ENSEMBLES).find(k => k.toLowerCase() === (genre || '').toLowerCase());

    // FIX: Map known sub-genres to parent ensembles if not found directly
    if (!genreKey) {
        const normalizedGenre = (genre || '').toLowerCase();
        if (normalizedGenre.includes('cinematic') || normalizedGenre.includes('orchestra') || normalizedGenre.includes('epic')) genreKey = 'Cinematic';
        else if (normalizedGenre.includes('romantic')) genreKey = 'Cinematic'; // Romantic maps to Cinematic ensembles
        else if (normalizedGenre.includes('rock') || normalizedGenre.includes('metal')) genreKey = 'Rock';
        else if (normalizedGenre.includes('jazz') || normalizedGenre.includes('swing')) genreKey = 'Jazz';
        else if (normalizedGenre.includes('electronic') || normalizedGenre.includes('techno')) genreKey = 'Electronic';
        else if (normalizedGenre.includes('impressionist') || normalizedGenre.includes('ethereal') || normalizedGenre.includes('pastoral') || normalizedGenre.includes('sci-fi')) genreKey = 'Ethereal';
        else if (normalizedGenre.includes('baroque') || normalizedGenre.includes('neo-classical')) genreKey = 'Baroque'; // Neo-Classical maps to Baroque
        else if (normalizedGenre.includes('horror') || normalizedGenre.includes('tension') || normalizedGenre.includes('gothic')) genreKey = 'Horror';
        else if (normalizedGenre.includes('minimalist') || normalizedGenre.includes('pulse')) genreKey = 'Minimalist';
        else if (normalizedGenre.includes('fantasy')) genreKey = 'Fantasy';
        else if (normalizedGenre.includes('action') || normalizedGenre.includes('chase')) genreKey = 'Action';
        else if (normalizedGenre.includes('western') || normalizedGenre.includes('americana')) genreKey = 'Rock'; // Fallback to Rock/Band
        else if (normalizedGenre.includes('noire') || normalizedGenre.includes('noir')) genreKey = 'Jazz';
        else if (normalizedGenre.includes('waltz')) genreKey = 'Cinematic'; // Fallback to Orch
        else if (normalizedGenre.includes('cyber') || normalizedGenre.includes('hybrid')) genreKey = 'Electronic';
        else if (normalizedGenre.includes('avant')) genreKey = 'Horror'; // Closest match
    }

    // Constraint Fix: Smart Ensemble Inference (Tag-Based Fallback)
    // If the genre is unknown (e.g. "Cyberpunk"), don't just default to Cinematic.
    // Instead, search the Instrument Library for matching tags.

    let selectedEnsemble = null;

    if (genreKey) {
        const ensembles = GENRE_ENSEMBLES[genreKey];

        // Fix: "Curated Filter" - If specific sub-genre keywords are present, filter candidates.
        let candidates = ensembles;
        const subGenre = (genre || '').toLowerCase();

        if (genreKey === 'Cinematic') {
            if (subGenre.includes('epic') || subGenre.includes('action')) {
                // Prefer Epic Orchestra for max range
                const epic = ensembles.find(e => e.id === 'epic_orchestra');
                if (epic) candidates = [epic];
                else {
                    candidates = ensembles.filter(e =>
                        e.instruments.includes('contrabass') ||
                        e.instruments.includes('tuba') ||
                        e.instruments.includes('bass_drop')
                    );
                }
            } else if (subGenre.includes('emotional') || subGenre.includes('drama') || subGenre.includes('romantic')) {
                candidates = ensembles.filter(e => e.id === 'emotional_strings');
            }
        } else if (genreKey === 'Horror') {
            if (subGenre.includes('gothic')) {
                candidates = ensembles.filter(e => e.id === 'gothic_cathedral');
            }
        }

        if (genreKey === 'Ethereal') {
            if (subGenre.includes('sci-fi') || subGenre.includes('cyber')) {
                candidates = ensembles.filter(e => e.id === 'cosmic_drift');
            }
        }

        if (candidates.length === 0) candidates = ensembles; // Fallback

        selectedEnsemble = random.pick(candidates);
        console.log(`[Orchestrator] Selected Ensemble: ${selectedEnsemble.name} for genre ${genre}`);
    } else {
        console.warn(`[Orchestrator] Unknown genre '${genre}'. Skipping Tag-Based Inference to use Minimal Fallback.`);
        // Note: Previously attempted inference, but it often yields too many results or random sets.
        // We now strict fallback to MINIMAL if genre is not explicit.
    }

    if (!selectedEnsemble) {
        // Fallback to Minimal Default (Max 4 instruments)
        // Fix: "Kitchen Sink Fallback" - Prevents unknown genres from loading full orchestra
        selectedEnsemble = MINIMAL_FALLBACK;
        console.log(`[Orchestrator] Fallback to Minimal Ensemble: ${selectedEnsemble.name}`);
    }

    const instruments = [];
    selectedEnsemble.instruments.forEach((instId, idx) => {
        // Resolve ID to Object
        // If ID ends with '_solo' or '_section', it refers to specific entries in INSTRUMENT_LIBRARY.
        // We use getInstrument from Registry.
        const instDef = getInstrument(instId);

        if (instDef) {
            // Clone and assign Instance ID
            instruments.push({
                ...instDef,
                instanceId: `${instId}_${idx}`, // Unique instance ID
                index: idx
            });
        } else {
            console.warn(`[Orchestrator] Unknown instrument ID in ensemble: ${instId}`);
        }
    });

    // --- HARD CONSTRAINT: Browser Performance Cap ---
    // Limit to 10 instruments total
    // FIX: Ensure Spectral Diversity (Low/High) is preserved during truncation
    if (instruments.length > 10) {
        // Prioritize by role to ensure functionality
        // Keep: Bass, Melody, Percussion, Harmony. Drop Color/Support.
        const priorityOrder = ['bass', 'lead', 'melody', 'percussion', 'harmony', 'pad', 'rhythm', 'counter_melody'];

        instruments.sort((a, b) => {
            const roleA = (a.roles && a.roles[0]) || 'support';
            const roleB = (b.roles && b.roles[0]) || 'support';
            const idxA = priorityOrder.indexOf(roleA);
            const idxB = priorityOrder.indexOf(roleB);

            // Spectral Boost: Give High Priority to Low/High spectral roles
            const spectralA = a.traits?.spectral_role;
            const spectralB = b.traits?.spectral_role;
            let scoreA = idxA === -1 ? 99 : idxA;
            let scoreB = idxB === -1 ? 99 : idxB;

            // Boost Low/High to ensure they survive cut
            if (spectralA === 'low' || spectralA === 'high') scoreA -= 2; // Artificial boost
            if (spectralB === 'low' || spectralB === 'high') scoreB -= 2;

            // If scores still equal, prefer Monophonic (cheaper)
            if (scoreA === scoreB) {
                return (a.type === 'polyphonic' ? 1 : 0) - (b.type === 'polyphonic' ? 1 : 0);
            }

            return scoreA - scoreB;
        });

        // Ensure at least one Low and one High instrument are in the top 10
        // (The sorting boost above should handle this, but let's verify)
        // Actually, sorting modifies array in place. Just trim.
        instruments.length = 10;
        console.warn(`[Orchestrator] Ensemble '${selectedEnsemble.name}' truncated to 10 instruments.`);
    }

    // Limit Polyphonic Instruments to 2 (CPU Protection)
    const polyphonic = instruments.filter(i => i.type === 'polyphonic' || (i.traits && i.traits.family === 'keys'));
    if (polyphonic.length > 2) {
        // Keep the best 2 (e.g. Piano + Strings Pad)
        // Sort by role priority
        const priorityOrder = ['harmony', 'pad', 'melody']; // Harmony needs polyphony most
        polyphonic.sort((a, b) => {
            const roleA = (a.roles && a.roles[0]) || 'support';
            const roleB = (b.roles && b.roles[0]) || 'support';
            return priorityOrder.indexOf(roleA) - priorityOrder.indexOf(roleB);
        });

        const kept = polyphonic.slice(0, 2);
        const removedIds = polyphonic.slice(2).map(i => i.instanceId);

        // Remove the extras from the main list
        // Or better, don't remove, just mark them as inactive? No, remove to save sampler loading.
        for (let i = instruments.length - 1; i >= 0; i--) {
            if (removedIds.includes(instruments[i].instanceId)) {
                instruments.splice(i, 1);
            }
        }
        console.warn(`[Orchestrator] Polyphonic limit enforced. Removed: ${removedIds.join(', ')}`);
    }

    return instruments;
};

// DEPRECATED: Stub for compatibility until GenerationPipeline is updated
// Returns a mock RoleMap that claims everything is active,
// delegating the actual filtering to the new Instrument Activation logic in RoleAllocator.
import { ABSTRACT_ROLES } from '../RoleAllocator.js';
export const getActiveRoles = (sectionType, energy, random, config) => {
    const roleMap = new Map();
    Object.values(ABSTRACT_ROLES).forEach(role => {
        roleMap.set(role, { active: true, intensity: 1.0, variation: 'default' });
    });
    return roleMap;
};
